import React, { useEffect, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { useDispatch } from 'react-redux';
import { savePermitForm, updatePermitForm } from '../../store/slices/projectPermitsSlice';
import { showToast } from '../utils/Toast';
import { fetchProjectData } from '../../utils/projectDataUtils';
import { printPermitForm, buildWarrantyCertificateContent } from '../../utils/PermitPrintUtils';
import FloatingActionBar from './FloatingActionBar';

const WARRANTY_TERMS = [
  'Pitched (Sloped) Roofing Systems: Seven (7) Years',
  'Flat Roofing / Low-Slope Roofing Systems: Five (5) Years',
  'Deck Coating / Waterproof Deck Systems: Five (5) Years'
];

const WARRANTY_COVERS = [
  'Workmanship-related installation defects',
  'Leaks caused directly by improper installation by Contractor',
  'Failure of installed systems due to Contractor\'s labor or installation methods'
];

const WARRANTY_EXCLUSIONS = [
  'Roof maintenance or routine service work',
  'Roof tune-ups',
  'Tile replacement',
  'Roof repairs',
  'Temporary or emergency repairs',
  'Work performed on areas not originally installed by Contractor',
  'Damage caused by normal wear and tear',
  'Damage resulting from foot traffic, abuse, other trades or neglect',
  'Damage caused by third parties or trades',
  'Structural movement, settlement, or substrate failure',
  'Water displacement or staining',
  'Improper building design, drainage issues, or pre-existing conditions'
];

const ACTS_OF_GOD = [
  'Windstorms',
  'Earthquakes',
  'Heavy rain or flooding',
  'Fire'
];

const OWNER_RESPONSIBILITIES = [
  'Full payment of all related invoices',
  'Proper and regular roof maintenance',
  'Keeping roof drains, scuppers, and gutters clear',
  'Preventing unauthorized access or alterations to the roof',
  'Promptly notifying Contractor in writing of any observed issues. Notifications must be made within 7 days of observance.',
  'Allowing Contractor reasonable access to inspect and perform warranty work'
];

const CLAIM_REQUIREMENTS = [
  'Property address',
  'Description of the issue',
  'Date the issue was first observed',
  'Photos'
];

const LIABILITY_LIMITS = [
  'Interior damage',
  'Consequential or incidental damages',
  'Loss of use',
  'Human damages',
  'Mold or mildew',
  'Any costs exceeding the original scope of work'
];

const formatDateForInput = (date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const year = date.getFullYear();
  return `${year}-${month}-${day}`;
};

const escapeHtml = (value) => {
  const stringValue = String(value ?? '');
  return stringValue
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const toHtmlList = (items) => items
  .map((item) => `<li>${escapeHtml(item)}</li>`)
  .join('');

const WarrantyCertificateForm = ({ projectId, currentPermit, onClose }) => {
  const dispatch = useDispatch();
  const signatureRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    propertyOwner: '',
    propertyAddress: '',
    installationDate: '',
    effectiveDate: '',
    materialManufacturer: '',
    printedNameTitle: '',
    signedDate: '',
    signatureImage: ''
  });

  const getSignatureImage = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      return signatureRef.current.toDataURL('image/png');
    }
    return formData.signatureImage || '';
  };

  useEffect(() => {
    if (currentPermit && currentPermit.formType === 'warrantyForm') {
      const savedData = currentPermit.formData || {};
      setFormData((prev) => ({
        ...prev,
        propertyOwner: savedData.propertyOwner || savedData.ownerName || '',
        propertyAddress: savedData.propertyAddress || '',
        installationDate: savedData.installationDate || savedData.dateOfInstallation || '',
        effectiveDate: savedData.effectiveDate || '',
        materialManufacturer: savedData.materialManufacturer || '',
        printedNameTitle: savedData.printedNameTitle || '',
        signedDate: savedData.signedDate || '',
        signatureImage: savedData.signatureImage || ''
      }));
      return;
    }

    const getProjectData = async () => {
      try {
        const projectData = await fetchProjectData(projectId);
        const actualProject = projectData.project || projectData;
        const today = formatDateForInput(new Date());

        let propertyAddress = '';
        if (actualProject.siteContact?.address) {
          const addr = actualProject.siteContact.address;
          const pieces = [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean);
          propertyAddress = pieces.join(', ');
        } else if (actualProject.address) {
          const addr = actualProject.address;
          const pieces = [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean);
          propertyAddress = pieces.join(', ');
        }

        setFormData((prev) => ({
          ...prev,
          propertyOwner: actualProject.client?.name || '',
          propertyAddress,
          installationDate: today,
          effectiveDate: today,
          signedDate: today
        }));
      } catch (error) {
        console.error('Error prefilling craftsmanship warranty form:', error);
      }
    };

    getProjectData();
  }, [currentPermit, projectId]);

  useEffect(() => {
    if (!signatureRef.current || !formData.signatureImage) return;
    try {
      signatureRef.current.fromDataURL(formData.signatureImage);
    } catch (error) {
      console.error('Unable to load saved signature image:', error);
    }
  }, [formData.signatureImage]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    setFormData((prev) => ({
      ...prev,
      signatureImage: ''
    }));
  };

  const handleDownload = () => {
    // Merge current in-memory formData with the live signature from the canvas
    const liveFormData = { ...formData, signatureImage: getSignatureImage() };
    const formContent = buildWarrantyCertificateContent(liveFormData);
    printPermitForm(liveFormData, 'Craftsmanship Warranty', formContent);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payloadFormData = {
      ...formData,
      signatureImage: getSignatureImage()
    };

    try {
      if (currentPermit && currentPermit._id) {
        await dispatch(updatePermitForm({
          projectId,
          permitId: currentPermit._id,
          formData: {
            ...currentPermit,
            formData: payloadFormData
          }
        })).unwrap();
        showToast('Craftsmanship Warranty updated successfully', 'success');
      } else {
        await dispatch(savePermitForm({
          projectId,
          formType: 'warrantyForm',
          formData: payloadFormData
        })).unwrap();
        showToast('Craftsmanship Warranty saved successfully', 'success');
      }
      onClose();
    } catch (error) {
      showToast(error.message || 'Failed to save Craftsmanship Warranty', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="permit-form-shell bg-white p-6 rounded-lg max-w-5xl mx-auto relative">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <img
            src="/logo/topsail-logo-small.png"
            alt="Topsail roofing"
            className="h-16 mr-4"
          />
          <div>
            <h2 className="text-2xl font-bold">Topsail roofing</h2>
            <p className="text-sm">QLTY CRAFTSMANSHIP</p>
          </div>
        </div>
        <div className="text-right">
          <p>205 E Avenida Magdalena</p>
          <p>San Clemente, CA 92672</p>
          <p>Phone: (949) 370-8612</p>
          <p className="uppercase">topsailroofing.com</p>
          <p>Lic# C-39 1070037</p>
        </div>
      </div>

      <h1 className="text-3xl font-bold text-center mb-6">CRAFTSMANSHIP WARRANTY</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div>
            <label className="block text-sm font-medium mb-1">Property Owner:</label>
            <input
              type="text"
              value={formData.propertyOwner}
              onChange={(e) => handleChange('propertyOwner', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Property Address:</label>
            <input
              type="text"
              value={formData.propertyAddress}
              onChange={(e) => handleChange('propertyAddress', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Installation Date:</label>
            <input
              type="date"
              value={formData.installationDate}
              onChange={(e) => handleChange('installationDate', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Effective Date:</label>
            <input
              type="date"
              value={formData.effectiveDate}
              onChange={(e) => handleChange('effectiveDate', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Material Manufacturer:</label>
            <input
              type="text"
              value={formData.materialManufacturer}
              onChange={(e) => handleChange('materialManufacturer', e.target.value)}
              className="w-full border border-gray-300 p-2 rounded"
            />
          </div>
        </div>

        <div className="space-y-6 text-sm text-gray-800 leading-relaxed">
          <section>
            <h2 className="text-lg font-bold mb-2">1. Warranty Coverage</h2>
            <p>
              Topsail Roofing ("Contractor") warrants that all roofing and waterproofing systems installed by Contractor will be free from defects in workmanship caused directly by Contractor during the applicable warranty period, subject to the terms, conditions, and exclusions outlined in this document.
            </p>
            <p className="mt-2 font-semibold">
              This is a craftsmanship warranty only. Material warranties, if available, are provided separately by the product manufacturer and are subject to the manufacturer's terms and conditions.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-2">2. Craftsmanship Warranty Terms by System Type</h2>
            <p>
              The following warranty periods apply from the date of substantial project completion, unless otherwise stated in writing:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {WARRANTY_TERMS.map((term) => (
                <li key={term}>{term}</li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-2">3. What This Warranty Covers</h2>
            <p>This craftsmanship warranty covers:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {WARRANTY_COVERS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mt-2">
              If a covered defect is confirmed, Contractor will, at its sole discretion, repair or correct the affected portion of the work at no cost for labor.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-2">4. What This Warranty Does NOT Cover</h2>
            <p>Unless expressly stated in writing, this warranty does not cover the following scopes of work:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {WARRANTY_EXCLUSIONS.map((item) => (
                <li key={item}>{item}</li>
              ))}
              <li>
                Damage caused by acts of God, including but not limited to:
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  {ACTS_OF_GOD.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </li>
            </ul>
            <p className="mt-2 font-semibold">
              Maintenance, tune-ups, tile replacement, and repairs are not warrantied unless otherwise stated in writing and signed by Contractor.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-2">5. Owner Responsibilities</h2>
            <p>To maintain warranty eligibility, the Owner is responsible for:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {OWNER_RESPONSIBILITIES.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mt-2">Failure to meet these responsibilities may void this warranty.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-2">6. Warranty Claims Procedure</h2>
            <p>All warranty claims must be submitted in writing to:</p>
            <p className="mt-2">
              <strong>Topsail Roofing</strong>
              <br />
              Email: info@topsailroofing.com
              <br />
              Phone: (949) 370-8612
            </p>
            <p className="mt-2">Claims must include:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {CLAIM_REQUIREMENTS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mt-2">
              Contractor will inspect the reported condition before determining coverage. No warranty work will be performed without Contractor's written authorization.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-2">7. Limitations of Liability</h2>
            <p>This craftsmanship warranty is limited strictly to the repair of defective workmanship.</p>
            <p className="mt-2">Contractor shall not be responsible for:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {LIABILITY_LIMITS.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mt-2">This warranty is non-transferable unless approved in writing by Contractor.</p>
          </section>

          <section>
            <h2 className="text-lg font-bold mb-2">8. Entire Agreement</h2>
            <p>
              This document represents the entire craftsmanship warranty provided by Topsail Roofing No verbal statements or representations modify this warranty unless made in writing and signed by an authorized representative of Contractor.
            </p>
          </section>
        </div>

        <div className="mt-10 border-t border-gray-300 pt-6">
          <p className="font-medium">Authorized by:</p>
          <p className="mb-4">Topsail Roofing</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-2">Signature:</label>
              <div className="border border-gray-300 rounded bg-white overflow-hidden">
                <SignatureCanvas
                  ref={signatureRef}
                  canvasProps={{
                    className: 'w-full h-32 md:h-40 touch-action-none'
                  }}
                />
              </div>
              <button
                type="button"
                onClick={handleClearSignature}
                className="mt-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Clear Signature
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Printed Name &amp; Title:</label>
              <input
                type="text"
                value={formData.printedNameTitle}
                onChange={(e) => handleChange('printedNameTitle', e.target.value)}
                className="w-full border border-gray-300 p-2 rounded"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Date:</label>
              <input
                type="date"
                value={formData.signedDate}
                onChange={(e) => handleChange('signedDate', e.target.value)}
                className="w-full border border-gray-300 p-2 rounded"
              />
            </div>
          </div>
        </div>

        <div className="pb-20"></div>
      </form>

      <FloatingActionBar
        onCancel={onClose}
        onDownload={handleDownload}
        onSubmit={handleSubmit}
        loading={loading}
        isUpdate={!!(currentPermit && currentPermit._id)}
      />
    </div>
  );
};

export default WarrantyCertificateForm;
